"use client";

import { useState, useEffect, useRef } from "react";

export function useMedia(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

export function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // If the element is already in the viewport on load, mark it visible immediately
    // so it doesn't stay hidden if the IntersectionObserver misses the initial intersection.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold, rootMargin: "300px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/** Returns [ref, animated] where animated flips to true once the element
 *  enters the viewport (30% visible). Fires once. */
export function useScrollAnimate(threshold = 0.3) {
  const ref = useRef(null);
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setAnimated(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setAnimated(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, animated];
}

/** Entrance animation. 0.4s/14px is the site-wide entrance register.
 *  The old 0.9s/28px default read as a theme animation playing at the visitor
 *  rather than the page arriving; the references we benchmark against run
 *  0.1-0.2s for interaction and stay well under 0.5s for entrances. */
export function FadeIn({ children, delay = 0, style = {}, duration = 0.4, distance = 14 }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      ...style,
      opacity: visible ? 1 : 0,
      // "none", not translateY(0): a persistent transform makes this wrapper a
      // containing block for position:fixed descendants (broke the screenshot
      // lightbox, 2026-08-01). Visually identical once the entrance finishes.
      transform: visible ? "none" : `translateY(${distance}px)`,
      transition: `opacity ${duration}s cubic-bezier(.16,1,.3,1) ${delay}s, transform ${duration}s cubic-bezier(.16,1,.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

/** Deprecated alias. FadeIn's own default is now this register, so this adds
 *  nothing; kept only so the ~30 existing /integrations call sites keep working.
 *  Collapse into FadeIn when the component layer is consolidated. */
export function FadeInFast(props) {
  return <FadeIn {...props} />;
}
